import { useCallback, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import { MARKDOWN_TILLAGG } from '../lib/markdown'
import type { Bildspel } from '../types'
import Illustration from './Illustration'

const BILDSPEL_KOMPONENTER: Components = {
  img: ({ src, alt }) => <Illustration src={typeof src === 'string' ? src : undefined} alt={alt} />,
}

/** Bläddringsbart bildspel i helskärm — lärarens föreläsningsunderlag.
 *  Markdown per sida, bläddring med knappar eller piltangenter, öppnas på
 *  lektionens avsnitt via startSida. */
export default function BildspelVy({
  bildspel,
  startSida,
  onStang,
}: {
  bildspel: Bildspel
  startSida: number
  onStang: () => void
}) {
  const [sida, setSida] = useState(Math.min(startSida, bildspel.sidor.length - 1))
  const antal = bildspel.sidor.length

  const byt = useCallback(
    (riktning: number) => {
      setSida((nu) => Math.max(0, Math.min(antal - 1, nu + riktning)))
    },
    [antal],
  )

  useEffect(() => {
    const vidTangent = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onStang()
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') byt(1)
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') byt(-1)
    }
    document.addEventListener('keydown', vidTangent)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', vidTangent)
      document.body.style.overflow = ''
    }
  }, [onStang, byt])

  return (
    <div className="projektion bildspel" role="dialog" aria-modal="true" aria-label="Bildspel">
      <div className="bildspel-sida" key={sida}>
        <ReactMarkdown components={BILDSPEL_KOMPONENTER} remarkPlugins={MARKDOWN_TILLAGG}>{bildspel.sidor[sida].markdown}</ReactMarkdown>
      </div>
      <div className="bildspel-kontroller">
        <button type="button" className="rund-knapp" onClick={() => byt(-1)} disabled={sida === 0} aria-label="Föregående bild">
          ←
        </button>
        <span className="raknare">
          {sida + 1} / {antal}
        </span>
        <button
          type="button"
          className="rund-knapp"
          onClick={() => byt(1)}
          disabled={sida === antal - 1}
          aria-label="Nästa bild"
        >
          →
        </button>
        <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={onStang}>
          Stäng ✕
        </button>
      </div>
    </div>
  )
}
